/**
 * Phase 67.3 — Template Gallery.
 *
 * Pre-built agent templates with one-click deploy. Each template includes:
 * - Name, icon (emoji), description
 * - System prompt
 * - Suggested tools
 * - Example conversation (2-3 messages)
 * - One-click deploy button
 * - Preview modal with example conversation
 */

import { useState } from "react";
import { createAgent, type Agent } from "../api/client.js";

// ── Template Definitions ─────────────────────────────────────────────────────

export interface ExampleMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AgentTemplateDefinition {
  id: string;
  name: string;
  icon: string;
  description: string;
  systemPrompt: string;
  suggestedTools: string[];
  exampleConversation: ExampleMessage[];
}

export const AGENT_TEMPLATES: AgentTemplateDefinition[] = [
  {
    id: "research-assistant",
    name: "Research Assistant",
    icon: "\uD83D\uDD0D",
    description: "I help you research topics thoroughly",
    systemPrompt:
      "You are a thorough research assistant. When given a topic, you break it down into key areas, find relevant information, synthesize findings, and present clear summaries with sources. You ask clarifying questions when the research scope is unclear and provide balanced perspectives on controversial topics.",
    suggestedTools: ["web_search", "file_read", "file_write"],
    exampleConversation: [
      { role: "user", content: "Research the current state of quantum computing" },
      {
        role: "assistant",
        content:
          "I'll research quantum computing across several key areas:\n\n**Current State:**\n- Major players: IBM, Google, and IonQ are leading hardware development\n- IBM's latest processor exceeds 1,000 qubits\n- Error correction remains the primary challenge\n\n**Key Applications:**\n- Drug discovery and molecular simulation\n- Cryptography and post-quantum security\n- Optimization problems in logistics and finance\n\nWould you like me to dive deeper into any of these areas?",
      },
      { role: "user", content: "Yes, tell me more about error correction" },
    ],
  },
  {
    id: "code-helper",
    name: "Code Helper",
    icon: "\uD83D\uDCBB",
    description: "I help you write and debug code",
    systemPrompt:
      "You are an expert software engineer. You help users write, debug, and optimize code across multiple programming languages. You explain your reasoning, follow best practices, write clean and well-documented code, and suggest improvements. When debugging, you systematically identify root causes and explain fixes clearly.",
    suggestedTools: ["code_execute", "file_read", "file_write"],
    exampleConversation: [
      { role: "user", content: "Write a function that finds duplicates in an array" },
      {
        role: "assistant",
        content:
          'Here\'s an efficient solution using a Set:\n\n```typescript\nfunction findDuplicates<T>(arr: T[]): T[] {\n  const seen = new Set<T>();\n  const duplicates = new Set<T>();\n  for (const item of arr) {\n    if (seen.has(item)) {\n      duplicates.add(item);\n    } else {\n      seen.add(item);\n    }\n  }\n  return Array.from(duplicates);\n}\n```\n\nThis runs in O(n) time and O(n) space. Would you like me to add tests?',
      },
      { role: "user", content: "Yes, add some test cases" },
    ],
  },
  {
    id: "customer-support",
    name: "Customer Support",
    icon: "\uD83D\uDE4B",
    description: "I handle customer inquiries professionally",
    systemPrompt:
      "You are a professional customer support agent. You handle inquiries with empathy, patience, and clarity. You listen carefully to customer concerns, provide accurate information, offer solutions, and escalate when appropriate. You maintain a friendly yet professional tone at all times and always confirm that the customer's issue has been resolved before closing a conversation.",
    suggestedTools: ["db_query", "file_read"],
    exampleConversation: [
      { role: "user", content: "I can't log into my account and I've tried resetting my password" },
      {
        role: "assistant",
        content:
          "I'm sorry to hear you're having trouble logging in. Let me help you with that.\n\nCould you tell me:\n1. The email address associated with your account\n2. Whether you received the password reset email\n3. Any error message you see when trying to log in\n\nThis will help me identify the issue and get you back into your account quickly.",
      },
    ],
  },
  {
    id: "data-analyst",
    name: "Data Analyst",
    icon: "\uD83D\uDCCA",
    description: "I analyze data and create visualizations",
    systemPrompt:
      "You are a skilled data analyst. You help users understand their data by performing analysis, creating visualizations, identifying trends and patterns, and providing actionable insights. You explain statistical concepts in plain language, validate data quality, and suggest appropriate analytical methods based on the data type and the question being asked.",
    suggestedTools: ["code_execute", "file_read", "db_query"],
    exampleConversation: [
      { role: "user", content: "I have a CSV with sales data. Can you help me find trends?" },
      {
        role: "assistant",
        content:
          "I'd be happy to analyze your sales data. To get started, could you share the CSV file? Once I have it, I'll:\n\n1. **Inspect the data** - check columns, types, and quality\n2. **Summary statistics** - mean, median, variance of key metrics\n3. **Trend analysis** - monthly/quarterly patterns, growth rates\n4. **Visualizations** - charts showing key insights\n\nPlease upload your file and I'll begin the analysis.",
      },
      { role: "user", content: "Here's the file. The columns are date, product, quantity, revenue." },
    ],
  },
  {
    id: "content-creator",
    name: "Content Creator",
    icon: "\u270D\uFE0F",
    description: "I help create blog posts, social media, emails",
    systemPrompt:
      "You are a versatile content creator. You help users write blog posts, social media content, marketing emails, and other written materials. You adapt your tone and style to the target audience, follow SEO best practices for web content, maintain brand consistency, and provide multiple drafts or variations when requested. You ask about target audience and goals before writing.",
    suggestedTools: ["web_search", "file_write"],
    exampleConversation: [
      { role: "user", content: "Write a LinkedIn post about our new product launch" },
      {
        role: "assistant",
        content:
          "Before I draft the post, a few quick questions:\n\n1. What's the product name and key features?\n2. Who is your target audience?\n3. What tone do you prefer - formal, casual, or somewhere in between?\n4. Any specific call-to-action?\n\nWith this info, I'll create an engaging post that drives interest and engagement.",
      },
      { role: "user", content: "It's an AI analytics tool for small businesses. Keep it casual." },
    ],
  },
];

/**
 * Get a template by its ID.
 */
export function getTemplateById(id: string): AgentTemplateDefinition | undefined {
  return AGENT_TEMPLATES.find((t) => t.id === id);
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface TemplateGalleryProps {
  onDeploy: (template: AgentTemplateDefinition, agent: Agent) => void;
  provider?: string;
  model?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export function TemplateGallery({ onDeploy, provider, model }: TemplateGalleryProps) {
  const [previewTemplate, setPreviewTemplate] = useState<AgentTemplateDefinition | null>(null);
  const [deploying, setDeploying] = useState<string | null>(null);
  const [deployError, setDeployError] = useState<string | null>(null);

  async function handleDeploy(template: AgentTemplateDefinition) {
    setDeploying(template.id);
    setDeployError(null);
    try {
      const agent = await createAgent({
        name: template.name,
        systemPrompt: template.systemPrompt,
        model: model ?? "claude-sonnet-4-20250514",
        provider: provider ?? undefined,
      });
      onDeploy(template, agent);
    } catch (err) {
      setDeployError(`Failed to deploy "${template.name}": ${(err as Error).message}`);
    } finally {
      setDeploying(null);
    }
  }

  return (
    <div>
      <h3 className="text-base font-semibold text-ink-900">Agent Templates</h3>
      <p className="mt-1 text-sm text-ink-500">
        Start quickly with a pre-built agent template. Click to preview or deploy.
      </p>

      {deployError && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {deployError}
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {AGENT_TEMPLATES.map((template) => (
          <div
            key={template.id}
            className="rounded-lg border border-ink-200 p-4 hover:border-ink-300 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div>
                <span className="text-2xl">{template.icon}</span>
                <h4 className="mt-2 text-sm font-medium text-ink-800">{template.name}</h4>
                <p className="mt-1 text-xs text-ink-500">{template.description}</p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1">
              {template.suggestedTools.map((tool) => (
                <span
                  key={tool}
                  className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] text-ink-600"
                >
                  {tool}
                </span>
              ))}
            </div>

            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setPreviewTemplate(template)}
                className="flex-1 rounded-md border border-ink-200 px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 transition-colors"
              >
                Preview
              </button>
              <button
                onClick={() => handleDeploy(template)}
                disabled={deploying === template.id}
                className="flex-1 rounded-md bg-ink-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-ink-800 disabled:opacity-50 transition-colors"
              >
                {deploying === template.id ? "Deploying..." : "Deploy"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Preview modal */}
      {previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-lg rounded-2xl bg-white shadow-2xl sm:mx-0">
            <div className="border-b border-ink-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{previewTemplate.icon}</span>
                  <h3 className="text-base font-semibold text-ink-900">
                    {previewTemplate.name}
                  </h3>
                </div>
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="text-ink-400 hover:text-ink-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              <p className="mt-1 text-sm text-ink-500">{previewTemplate.description}</p>
            </div>

            <div className="px-6 py-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-400">
                System Prompt
              </h4>
              <p className="mt-1 text-sm text-ink-700 leading-relaxed">
                {previewTemplate.systemPrompt}
              </p>

              <h4 className="mt-4 text-xs font-semibold uppercase tracking-wider text-ink-400">
                Suggested Tools
              </h4>
              <div className="mt-1 flex flex-wrap gap-1">
                {previewTemplate.suggestedTools.map((tool) => (
                  <span
                    key={tool}
                    className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600"
                  >
                    {tool}
                  </span>
                ))}
              </div>

              <h4 className="mt-4 text-xs font-semibold uppercase tracking-wider text-ink-400">
                Example Conversation
              </h4>
              <div className="mt-2 space-y-2">
                {previewTemplate.exampleConversation.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        msg.role === "user"
                          ? "bg-ink-900 text-white"
                          : "bg-surface-2 text-ink-800"
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-ink-100 px-6 py-4">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="rounded-lg px-4 py-2 text-sm text-ink-600 hover:bg-ink-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setPreviewTemplate(null);
                  handleDeploy(previewTemplate);
                }}
                disabled={deploying !== null}
                className="rounded-lg bg-ink-900 px-4 py-2 text-sm font-medium text-white hover:bg-ink-800 disabled:opacity-50 transition-colors"
              >
                Deploy This Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
